<template>
  <div
    class="offcanvas offcanvas-fullscreen-sm-down offcanvas-end"
    tabindex="-1"
    ref="offcanvas"
    data-bs-backdrop="static"
    style="width: 480px; max-width: 100%;"
  >
    <!-- Header -->
    <div class="offcanvas-header bg-info text-dark py-3">
      <div>
        <h5 class="offcanvas-title fw-bold mb-0">
          <i class="fa-solid fa-object-group me-2" />
          {{ $t('Merge Session') }}
        </h5>
        <div class="small opacity-75 mt-1">
          {{ $t('Into') }}: {{ host ? hostTitle : '...' }}
        </div>
      </div>
      <button type="button" class="btn-close" data-bs-dismiss="offcanvas" />
    </div>

    <div class="offcanvas-body p-3" style="background: #f7f9fc;">

      <!-- Loading -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-info" role="status">
          <span class="visually-hidden">{{ $t('Loading') }}...</span>
        </div>
        <div class="text-muted small mt-2">{{ $t('Loading') }}...</div>
      </div>

      <template v-else>
        <!-- Host (destination) -->
        <div class="bg-white rounded-3 p-3 shadow-sm mb-3">
          <div class="d-flex align-items-center gap-3">
            <div
              class="d-flex align-items-center justify-content-center rounded-3 bg-info bg-opacity-10 text-info"
              style="width: 48px; height: 48px;"
            >
              <i class="fa-solid fa-bullseye fs-5" />
            </div>
            <div class="flex-grow-1" style="min-width: 0;">
              <div class="text-muted" style="font-size: 0.7rem;">{{ $t('Host (keeps its bill)') }}</div>
              <div class="fw-bold text-truncate">{{ hostTitle }}</div>
              <div v-if="hostSubtitle" class="text-muted text-truncate" style="font-size: 0.75rem;">
                {{ hostSubtitle }}
              </div>
            </div>
            <div class="text-end">
              <div class="text-muted" style="font-size: 0.7rem;">{{ $t('Total') }}</div>
              <div class="fw-bold text-info">{{ $storeIndex.formatThaiCurrency(host ? host.total : 0) }}</div>
            </div>
          </div>
        </div>

        <!-- Arrow -->
        <div class="text-center my-2">
          <i class="fa-solid fa-arrow-down text-info fs-4" />
        </div>

        <!-- Search -->
        <div class="mb-3">
          <div class="input-group">
            <span class="input-group-text bg-white border-end-0">
              <i class="fa-solid fa-magnifying-glass text-muted" />
            </span>
            <input
              type="text"
              class="form-control border-start-0 shadow-none"
              :placeholder="$t('Search') + '...'"
              v-model="search"
            />
          </div>
        </div>

        <!-- Candidate sessions -->
        <div class="mb-2 px-1 d-flex align-items-center justify-content-between">
          <span class="fw-bold text-dark small">
            <i class="fa-solid fa-people-group me-1 text-muted" />
            {{ $t('Select sessions to pull in') }}
          </span>
          <span class="badge bg-info-subtle text-info rounded-pill">
            {{ selectedIds.length }} / {{ filteredCandidates.length }}
          </span>
        </div>

        <div v-if="filteredCandidates.length === 0" class="text-center text-muted py-4">
          <i class="fa-solid fa-circle-xmark fa-2x opacity-25 mb-2" />
          <p class="small mb-0">{{ $t('No other open sessions to merge') }}</p>
        </div>

        <div
          v-for="c in filteredCandidates"
          :key="c.id"
          class="bg-white rounded-3 p-3 shadow-sm mb-2 border-2"
          :class="isSelected(c.id) ? 'border-info' : 'border border-transparent'"
          style="cursor: pointer; transition: all 0.15s ease;"
          @click="toggle(c.id)"
        >
          <div class="d-flex align-items-center gap-2">
            <div class="form-check mb-0">
              <input
                type="checkbox"
                class="form-check-input"
                :checked="isSelected(c.id)"
                @click.stop
                @change="toggle(c.id)"
              />
            </div>
            <div
              class="d-flex align-items-center justify-content-center rounded-circle"
              :class="isSelected(c.id) ? 'bg-info text-white' : 'bg-secondary bg-opacity-10 text-secondary'"
              style="width: 38px; height: 38px; flex-shrink: 0;"
            >
              <i :class="typeIcon(c.type)" style="font-size: 0.85rem;" />
            </div>
            <div style="flex: 1; min-width: 0;">
              <div class="d-flex align-items-center gap-2">
                <span class="fw-bold" style="font-size: 0.95rem;">{{ sessionNo(c.session_no) }}</span>
                <span class="badge bg-light text-secondary border fw-normal" style="font-size: 0.65rem;">
                  <i :class="typeIcon(c.type)" class="me-1" />{{ $t(typeLabel(c.type)) }}
                </span>
              </div>
              <div v-if="c.table_name" class="text-muted text-truncate" style="font-size: 0.75rem;">
                <i class="fa-solid fa-chair me-1" />{{ $t('Table') }} {{ c.table_name }}
              </div>
              <div v-if="c.customer_name" class="text-secondary text-truncate" style="font-size: 0.75rem;">
                <i class="fa-solid fa-user me-1" />{{ c.customer_name }}
              </div>
            </div>
            <div class="text-end">
              <div class="fw-bold" style="font-size: 0.9rem;">{{ $storeIndex.formatThaiCurrency(c.total) }}</div>
            </div>
          </div>
        </div>

        <!-- Info: what happens next -->
        <div class="alert alert-info bg-info bg-opacity-10 border-0 rounded-3 mt-3 mb-0 small text-dark">
          <i class="fa-solid fa-circle-info me-2" />
          {{ $t('After merging all orders move under the host. Use Check Bill to combine into one bill and take payment.') }}
        </div>
      </template>

    </div>

    <!-- Footer -->
    <div class="bg-white p-3 border-top">
      <div v-if="selectedIds.length" class="d-flex align-items-center justify-content-between mb-3 px-1">
        <span class="text-muted small">{{ $t('Combined total') }}:</span>
        <span class="fw-bold text-info">{{ $storeIndex.formatThaiCurrency(combinedTotal) }}</span>
      </div>
      <button
        class="btn btn-info w-100 py-3 fw-bold fs-6 text-white"
        style="border-radius: 12px;"
        :disabled="selectedIds.length === 0 || submitting"
        @click="handleMerge"
      >
        <i class="fa-solid fa-spinner fa-spin me-2" v-if="submitting" />
        <i class="fa-solid fa-object-group me-2" v-else />
        {{ $t('Merge Session') }}
        <span v-if="selectedIds.length" class="ms-1">({{ selectedIds.length }})</span>
      </button>
    </div>
  </div>
</template>

<script>
import { Sessions } from '@/services/endpoint/pos.service';
import { useScrollRestore } from '@/composables/useScrollRestore';

export default {
  name: 'OffcanvasMergeSession',
  props: {
    // Host session — the survivor that keeps its bill number/customer.
    hostToken: { type: String, required: true }
  },
  emits: ['merged', 'close'],
  data() {
    return {
      loading: true,
      submitting: false,
      search: '',
      host: null,
      candidates: [],
      selectedIds: []
    };
  },
  computed: {
    filteredCandidates() {
      const kw = this.search.trim().toLowerCase();
      if (!kw) return this.candidates;
      return this.candidates.filter(c =>
        this.sessionNo(c.session_no).toLowerCase().includes(kw)
        || (c.table_name || '').toLowerCase().includes(kw)
        || (c.customer_name || '').toLowerCase().includes(kw)
      );
    },
    hostTitle() {
      return this.host ? this.sessionNo(this.host.session_no) : '';
    },
    hostSubtitle() {
      if (!this.host) return '';
      const parts = [];
      if (this.host.table_name) parts.push(this.$t('Table') + ' ' + this.host.table_name);
      if (this.host.customer_name) parts.push(this.host.customer_name);
      return parts.join(' · ');
    },
    combinedTotal() {
      const sources = this.candidates
        .filter(c => this.selectedIds.includes(c.id))
        .reduce((sum, c) => sum + (Number(c.total) || 0), 0);
      return (Number(this.host?.total) || 0) + sources;
    }
  },
  methods: {
    sessionNo(no) {
      return '#' + String(no || 0).padStart(4, '0');
    },
    typeIcon(type) {
      const map = {
        dine_in: 'fa-solid fa-utensils',
        takeaway: 'fa-solid fa-bag-shopping',
        delivery: 'fa-solid fa-motorcycle'
      };
      return map[type] || 'fa-solid fa-receipt';
    },
    typeLabel(type) {
      const map = { dine_in: 'Dine-in', takeaway: 'Takeaway', delivery: 'Delivery' };
      return map[type] || type;
    },
    isSelected(id) {
      return this.selectedIds.includes(id);
    },
    toggle(id) {
      const idx = this.selectedIds.indexOf(id);
      if (idx >= 0) this.selectedIds.splice(idx, 1);
      else this.selectedIds.push(id);
    },
    async fetchCandidates() {
      this.loading = true;
      try {
        const res = await Sessions.mergeSessionList(this.hostToken);
        const data = res.data.data || {};
        this.host = data.host || null;
        this.candidates = data.sessions || [];
      } catch (err) {
        this.host = null;
        this.candidates = [];
        this.$toast.warn(err?.response?.data?.message || this.$t('Failed to merge tables'));
      } finally {
        this.loading = false;
      }
    },
    async handleMerge() {
      if (!this.selectedIds.length) return;
      this.submitting = true;
      try {
        await Sessions.mergeSession(this.hostToken, {
          source_session_ids: [...this.selectedIds]
        });
        this.$toast.success(
          '<b>' + this.$t('Success') + '</b> '
          + this.$t('Merged into') + ' ' + this.hostTitle
        );
        this.$emit('merged');
        $(this.$refs.offcanvas).offcanvas('hide');
      } catch (err) {
        this.$toast.warn(err?.response?.data?.message || this.$t('Failed to merge tables'));
      } finally {
        this.submitting = false;
      }
    }
  },
  mounted() {
    $(this.$refs.offcanvas).offcanvas('show');
    this.fetchCandidates();
    $(this.$refs.offcanvas).on('hidden.bs.offcanvas', () => {
      this.$emit('close');
    });
    useScrollRestore(() => this.$refs.offcanvas?.querySelector('.offcanvas-body'));
  }
};
</script>

<style scoped>
/*
  Split search box (icon addon + input with border-start-0): on focus Bootstrap
  only recolors the input's border, leaving the icon addon grey — so the blue
  border looks "incomplete" on the left. Recolor the whole group together.
*/
.input-group:focus-within .input-group-text,
.input-group:focus-within .form-control {
  border-color: #86b7fe;
}
</style>
